<template>
    <div class="py-12">
        <AiChatTabs />

        <teleport to="body">
            <div
                v-if="guideOpen && instructions"
                class="fixed inset-0 z-[600] flex items-center justify-center p-4"
            >
                <div
                    class="absolute inset-0 bg-black/40"
                    @click="guideOpen = false"
                />
                <div
                    class="relative flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
                >
                    <div
                        class="flex items-center justify-between border-b border-gray-200 p-5"
                    >
                        <h2 class="text-lg font-medium text-gray-800">
                            {{ t("vue.ai_skill_guide") }}
                        </h2>
                        <button
                            type="button"
                            class="cursor-pointer text-gray-400 transition hover:text-gray-700"
                            :aria-label="t('vue.close')"
                            @click="guideOpen = false"
                        >
                            <XMarkIcon class="h-5 w-5" />
                        </button>
                    </div>
                    <div class="flex-1 space-y-5 overflow-y-auto p-6">
                        <div class="flex justify-center pt-2">
                            <div class="relative">
                                <transition name="guide-bubble" mode="out-in">
                                    <div
                                        :key="bubbleIndex"
                                        class="absolute -top-1 left-full z-10 ml-2 whitespace-nowrap rounded-2xl rounded-bl-sm border border-[#FF421E]/20 bg-[#FF421E]/10 px-3 py-1.5 text-sm font-medium text-[#FF421E] shadow-sm"
                                    >
                                        {{ bubbles[bubbleIndex] }}
                                    </div>
                                </transition>
                                <div class="guide-robot">
                                    <AnimatedRobot />
                                </div>
                            </div>
                        </div>

                        <p
                            class="mx-auto max-w-md text-center text-sm text-gray-600"
                        >
                            {{ t("vue.ai_skill_guide_intro") }}
                        </p>

                        <div
                            class="rounded-xl border border-gray-200 bg-white p-5 text-[0.95rem] leading-relaxed text-gray-700"
                        >
                            <AiChatMarkdown :content="instructions" />
                        </div>
                    </div>
                    <div
                        class="flex items-center justify-end border-t border-gray-200 p-4"
                    >
                        <AtomicButton
                            color="yellow"
                            size="sm"
                            @click="copyGuide"
                        >
                            <span class="inline-flex items-center gap-1.5">
                                <ClipboardDocumentIcon class="h-4 w-4" />
                                {{
                                    guideCopied
                                        ? t("vue.ai_skill_copied")
                                        : t("vue.ai_skill_copy")
                                }}
                            </span>
                        </AtomicButton>
                    </div>
                </div>
            </div>
        </teleport>

        <div class="relative">
            <!-- Decorative animated robot backdrop, softened by the frosted panels. -->
            <div
                class="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden"
            >
                <div class="robot-backdrop">
                    <AnimatedRobot />
                </div>
            </div>

            <div class="relative z-10 grid gap-6 lg:grid-cols-[360px_1fr]">
                <!-- Sidebar: new + search + filter + list -->
                <div
                    class="flex h-[70vh] flex-col rounded-xl border border-gray-200 bg-white/75 backdrop-blur-md"
                >
                    <div class="border-b border-gray-200 p-4">
                        <AtomicButton
                            color="yellow"
                            size="sm"
                            class="mb-2 w-full"
                            @click="startCreate"
                        >
                            <span class="inline-flex items-center gap-1.5">
                                <PlusIcon class="h-4 w-4" />
                                {{ t("vue.ai_skill_new") }}
                            </span>
                        </AtomicButton>

                        <button
                            v-if="instructions"
                            type="button"
                            class="mb-3 inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                            @click="guideOpen = true"
                        >
                            <BookOpenIcon class="h-4 w-4" />
                            {{ t("vue.ai_skill_guide") }}
                        </button>

                        <div class="relative flex items-center gap-2">
                            <TextInput
                                v-model="search"
                                class="flex-1"
                                :placeholder="t('vue.ai_skill_search')"
                            />
                            <button
                                type="button"
                                class="relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition hover:bg-gray-50"
                                :class="{
                                    'border-[#FF421E] text-[#FF421E]':
                                        selectedTagIds.length > 0,
                                }"
                                :title="t('vue.ai_tags_filter')"
                                @click="filterOpen = !filterOpen"
                            >
                                <FunnelIcon class="h-5 w-5" />
                                <span
                                    v-if="selectedTagIds.length > 0"
                                    class="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF421E] px-1 text-[10px] font-semibold text-white"
                                >
                                    {{ selectedTagIds.length }}
                                </span>
                            </button>

                            <div
                                v-if="filterOpen"
                                class="absolute right-0 top-full z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
                            >
                                <p
                                    v-if="filterableTags.length === 0"
                                    class="px-2 py-2 text-xs text-gray-400"
                                >
                                    {{ t("vue.ai_tags_none") }}
                                </p>
                                <label
                                    v-for="tag in filterableTags"
                                    :key="tag.id ?? tag.slug"
                                    class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-gray-50"
                                >
                                    <input
                                        type="checkbox"
                                        :value="tag.id"
                                        :checked="
                                            selectedTagIds.includes(
                                                tag.id as number,
                                            )
                                        "
                                        class="h-4 w-4 rounded border-gray-300 text-[#FF421E] focus:ring-[#FF421E]"
                                        @change="toggleFilter(tag.id as number)"
                                    />
                                    <span class="text-sm text-gray-700">{{
                                        tag.name
                                    }}</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="flex-1 overflow-y-auto p-2">
                        <p
                            v-if="items.length === 0"
                            class="px-2 py-4 text-sm text-gray-400"
                        >
                            {{ t("vue.ai_skill_empty") }}
                        </p>

                        <inertia-link
                            v-for="item in items"
                            :key="item.id"
                            :href="linkFor(item.id)"
                            preserve-scroll
                            preserve-state
                            class="mb-1 block w-full rounded-lg px-3 py-2 text-left transition-colors"
                            :class="
                                selected &&
                                selected.id === item.id &&
                                mode !== 'create'
                                    ? 'bg-gray-100'
                                    : 'hover:bg-gray-50'
                            "
                        >
                            <span
                                class="block truncate text-sm font-medium text-gray-800"
                            >
                                {{ item.name }}
                            </span>
                            <div
                                v-if="item.tags && item.tags.length > 0"
                                class="mt-1 flex max-h-[1.75rem] flex-wrap items-center gap-1 overflow-hidden"
                            >
                                <AiTagChip
                                    v-for="tag in item.tags.slice(
                                        0,
                                        MAX_VISIBLE_TAGS,
                                    )"
                                    :key="tag.id ?? tag.slug"
                                    :label="tag.name"
                                />
                                <span
                                    v-if="item.tags.length > MAX_VISIBLE_TAGS"
                                    class="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500"
                                >
                                    +{{ item.tags.length - MAX_VISIBLE_TAGS }}
                                </span>
                            </div>
                            <span class="mt-0.5 block text-xs text-gray-400">
                                {{ formatDate(item.created_at) }}
                            </span>
                        </inertia-link>
                    </div>
                </div>

                <!-- Detail / editor -->
                <div
                    class="flex h-[70vh] flex-col rounded-xl border border-gray-200 bg-white/75 backdrop-blur-md"
                >
                    <!-- Create / edit form -->
                    <template v-if="mode === 'create' || mode === 'edit'">
                        <div class="border-b border-gray-200 p-5">
                            <h2 class="text-lg font-medium text-gray-800">
                                {{
                                    mode === "create"
                                        ? t("vue.ai_skill_new")
                                        : t("vue.ai_skill_edit")
                                }}
                            </h2>
                        </div>
                        <div class="flex-1 space-y-4 overflow-y-auto p-6">
                            <div>
                                <label
                                    class="text-xs font-medium uppercase tracking-wide text-gray-400"
                                >
                                    {{ t("vue.ai_skill_name") }}
                                </label>
                                <TextInput
                                    v-model="form.name"
                                    class="mt-1 w-full"
                                    maxlength="200"
                                    :placeholder="
                                        t('vue.ai_skill_name_placeholder')
                                    "
                                />
                            </div>
                            <div>
                                <label
                                    class="text-xs font-medium uppercase tracking-wide text-gray-400"
                                >
                                    {{ t("vue.ai_skill_summary") }}
                                </label>
                                <TextInput
                                    v-model="form.summary"
                                    class="mt-1 w-full"
                                    maxlength="500"
                                    :placeholder="
                                        t('vue.ai_skill_summary_placeholder')
                                    "
                                />
                            </div>
                            <div>
                                <label
                                    class="text-xs font-medium uppercase tracking-wide text-gray-400"
                                >
                                    {{ t("vue.ai_skill_content") }}
                                </label>
                                <TextAreaInput
                                    v-model="form.content"
                                    class="mt-1 w-full font-mono text-xs"
                                    :rows="14"
                                    :maxlength="maxContentChars"
                                    :placeholder="
                                        t('vue.ai_skill_content_placeholder')
                                    "
                                />
                                <p
                                    class="mt-1 text-right text-xs text-gray-400"
                                >
                                    {{ form.content.length }} /
                                    {{ maxContentChars }}
                                </p>
                            </div>
                        </div>
                        <AiTagEditor
                            v-if="mode === 'edit' && selected"
                            :entity-key="selected.id"
                            :submit-url="
                                route('ai-skills.tags.update', {
                                    skill: selected.id,
                                })
                            "
                            :tags="selected.tags"
                            :available-tags="availableTags"
                        />
                        <div
                            class="flex items-center justify-end gap-3 border-t border-gray-200 p-4"
                        >
                            <button
                                type="button"
                                class="cursor-pointer rounded-lg px-3 py-1.5 text-sm text-gray-500 transition hover:text-gray-700"
                                @click="cancelForm"
                            >
                                {{ t("vue.ai_skill_cancel") }}
                            </button>
                            <AtomicButton
                                color="yellow"
                                size="sm"
                                :disabled="saveDisabled"
                                @click="save"
                            >
                                {{ t("vue.ai_skill_save") }}
                            </AtomicButton>
                        </div>
                    </template>

                    <!-- View -->
                    <template v-else-if="selected">
                        <div
                            class="flex items-start justify-between gap-4 border-b border-gray-200 p-5"
                        >
                            <div class="min-w-0">
                                <h2 class="text-lg font-medium text-gray-800">
                                    {{ selected.name }}
                                </h2>
                                <p
                                    v-if="selected.summary"
                                    class="mt-1 text-sm text-gray-500"
                                >
                                    {{ selected.summary }}
                                </p>
                                <p class="mt-1 text-xs text-gray-400">
                                    {{ formatDate(selected.created_at) }}
                                </p>
                            </div>
                            <div class="flex shrink-0 items-center gap-3">
                                <button
                                    type="button"
                                    class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                                    @click="startEdit"
                                >
                                    <PencilSquareIcon class="h-4 w-4" />
                                    {{ t("vue.ai_skill_edit") }}
                                </button>
                                <button
                                    type="button"
                                    class="cursor-pointer text-gray-400 transition hover:text-custom-alert-100"
                                    @click="remove(selected.id)"
                                >
                                    <TrashIcon class="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        <div class="flex-1 overflow-y-auto p-6">
                            <AiChatMarkdown :content="selected.content" />
                        </div>
                        <AiTagEditor
                            :entity-key="selected.id"
                            :submit-url="
                                route('ai-skills.tags.update', {
                                    skill: selected.id,
                                })
                            "
                            :tags="selected.tags"
                            :available-tags="availableTags"
                        />
                    </template>

                    <div
                        v-else
                        class="flex h-full items-center justify-center p-6 text-center text-sm text-gray-400"
                    >
                        {{ t("vue.ai_skill_select") }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, reactive, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    TrashIcon,
    FunnelIcon,
    PlusIcon,
    PencilSquareIcon,
    BookOpenIcon,
    ClipboardDocumentIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { IAiTag } from "@/types/AiChat/IAiTag";
import type { IAiSkillDetail, IAiSkillListItem } from "@/types/AiChat/IAiSkill";

const props = defineProps<{
    items: IAiSkillListItem[];
    q: string | null;
    activeTagIds: number[];
    availableTags: IAiTag[];
    selected: IAiSkillDetail | null;
    instructions: string | null;
}>();

const { t } = useTranslations();

const guideOpen = ref(false);
const guideCopied = ref(false);

const bubbles = computed(() => [
    t("vue.ai_skill_guide_bubble_1"),
    t("vue.ai_skill_guide_bubble_2"),
    t("vue.ai_skill_guide_bubble_3"),
]);
const bubbleIndex = ref(0);
let bubbleTimer: number | undefined;

const stopBubbles = (): void => {
    if (bubbleTimer !== undefined) {
        window.clearInterval(bubbleTimer);
        bubbleTimer = undefined;
    }
};

watch(guideOpen, (open) => {
    if (open) {
        bubbleIndex.value = 0;
        stopBubbles();
        bubbleTimer = window.setInterval(() => {
            bubbleIndex.value = (bubbleIndex.value + 1) % bubbles.value.length;
        }, 2600);
    } else {
        stopBubbles();
    }
});

onUnmounted(stopBubbles);

// navigator.clipboard is only available in a secure context; fall back to a
// hidden textarea + execCommand so copy works on plain-HTTP/local setups too.
const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
        if (
            typeof navigator !== "undefined" &&
            navigator.clipboard &&
            window.isSecureContext
        ) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // fall through to the execCommand fallback
    }

    try {
        const area = document.createElement("textarea");
        area.value = text;
        area.style.position = "fixed";
        area.style.top = "-9999px";
        area.setAttribute("readonly", "");
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(area);
        return ok;
    } catch {
        return false;
    }
};

const copyGuide = async (): Promise<void> => {
    if (props.instructions === null) {
        return;
    }
    if (await copyToClipboard(props.instructions)) {
        guideCopied.value = true;
        window.setTimeout(() => {
            guideCopied.value = false;
        }, 2000);
    }
};

const MAX_VISIBLE_TAGS = 3;
const maxContentChars = 8000;

type Mode = "view" | "edit" | "create";
const mode = ref<Mode>("view");

const form = reactive({ name: "", summary: "", content: "" });

const saveDisabled = computed(
    () => form.name.trim() === "" || form.content.trim() === "",
);

const startCreate = (): void => {
    form.name = "";
    form.summary = "";
    form.content = "";
    mode.value = "create";
};

const startEdit = (): void => {
    if (!props.selected) {
        return;
    }
    form.name = props.selected.name;
    form.summary = props.selected.summary ?? "";
    form.content = props.selected.content;
    mode.value = "edit";
};

const cancelForm = (): void => {
    mode.value = "view";
};

// A fresh server "selected" (after a save/navigation) returns us to view mode.
watch(
    () => props.selected?.id,
    () => {
        mode.value = "view";
    },
);

const save = (): void => {
    if (saveDisabled.value) {
        return;
    }

    const payload = {
        name: form.name.trim(),
        summary: form.summary.trim(),
        content: form.content,
    };

    if (mode.value === "create") {
        router.post(route("ai-skills.store"), payload, {
            preserveScroll: true,
            onSuccess: () => {
                mode.value = "view";
            },
        });
        return;
    }

    if (props.selected) {
        router.put(
            route("ai-skills.update", { skill: props.selected.id }),
            payload,
            {
                preserveScroll: true,
                onSuccess: () => {
                    mode.value = "view";
                },
            },
        );
    }
};

const search = ref(props.q ?? "");
const selectedTagIds = ref<number[]>([...props.activeTagIds]);
const filterOpen = ref(false);
let searchTimer: number | undefined;

const filterableTags = computed<IAiTag[]>(() =>
    props.availableTags.filter((tag) => tag.id !== null),
);

const visit = (): void => {
    router.get(
        route("ai-skills.index"),
        {
            q: search.value,
            tags: selectedTagIds.value,
            skill: props.selected?.id,
        },
        { preserveState: true, preserveScroll: true },
    );
};

const linkFor = (id: number): string =>
    route("ai-skills.index", {
        skill: id,
        q: search.value,
        tags: selectedTagIds.value,
    });

const toggleFilter = (id: number): void => {
    selectedTagIds.value = selectedTagIds.value.includes(id)
        ? selectedTagIds.value.filter((current) => current !== id)
        : [...selectedTagIds.value, id];
    visit();
};

watch(search, () => {
    if (searchTimer !== undefined) {
        window.clearTimeout(searchTimer);
    }
    searchTimer = window.setTimeout(visit, 300);
});

const remove = (id: number): void => {
    router.delete(route("ai-skills.destroy", { skill: id }), {
        preserveScroll: true,
    });
};

const formatDate = (value: string | null): string => {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleString();
};
</script>

<style scoped>
.robot-backdrop {
    opacity: 0.3;
    filter: grayscale(25%) blur(9px);
}

.robot-backdrop :deep(.robot-anim),
.robot-backdrop :deep(.robot-anim svg) {
    width: 540px !important;
    height: 540px !important;
}

.robot-backdrop :deep(#robotMessage) {
    display: none !important;
}

.guide-robot {
    width: 72px;
    height: 72px;
    animation: guideFloat 3.2s ease-in-out infinite;
    will-change: transform;
}

@keyframes guideFloat {
    0%,
    100% {
        transform: translateY(0) rotate(0deg);
    }
    35% {
        transform: translateY(-8px) rotate(-3deg);
    }
    65% {
        transform: translateY(-3px) rotate(3deg);
    }
}

.guide-robot :deep(.robot-anim),
.guide-robot :deep(.robot-anim svg) {
    width: 72px !important;
    height: 72px !important;
}

.guide-robot :deep(#robotMessage) {
    display: none !important;
}

.guide-bubble-enter-active,
.guide-bubble-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.guide-bubble-enter-from,
.guide-bubble-leave-to {
    opacity: 0;
    transform: translateY(6px) scale(0.9);
}

@media (prefers-reduced-motion: reduce) {
    .guide-robot {
        animation: none !important;
    }
}
</style>
